/**
 * State Farm "Ensighten Privacy" cross-domain cookies.
 *
 * When a visitor uses the State Farm Do Not Sell or Share preference center
 * (linked from our footer, hosted on statefarm.com) and clicks Save, State
 * Farm's machinery writes four persistent cookies on BOTH `.statefarm.com`
 * AND `.statefarmgamerhood.com`. Each cookie carries `'1'` (allowed) or
 * `'0'` (opted out) for its corresponding DNS-page category:
 *
 *   STATEFARM_ENSIGHTEN_PRIVACY_Analytics    ←  Analytics toggle
 *   STATEFARM_ENSIGHTEN_PRIVACY_Marketing    ←  Online Behavioral Advertising
 *   STATEFARM_ENSIGHTEN_PRIVACY_Performance  ←  Performance
 *   STATEFARM_ENSIGHTEN_PRIVACY_Social       ←  Social
 *
 * Cookies are Secure, NOT HttpOnly (the DEEL script itself reads them
 * client-side), persistent (~1 year), and present on the gamerhood domain
 * so document.cookie can read them on every page load.
 *
 * What we do with them, today (per the plan in
 * ~/.claude/plans/valiant-wobbling-ocean.md):
 *
 *   - Read `_Analytics` and use it to inform GTM's `analytics_storage`
 *     state at runtime when no higher-priority signal exists. See
 *     consent.ts:runSfCookieSync.
 *   - Read `_Marketing` ONLY as a load gate for the State Farm enterprise
 *     GTM container (added 2026-07; SF's "applicable privacy indicator"
 *     for their retargeting container - see consent.ts:syncSfGtm and the
 *     SFGTM block in index.html). We still do NOT propagate it to GTM
 *     `ad_storage`: the marketing toggle on /privacy-settings stays the
 *     authoritative source for our own `ad_storage` state, and the DEEL
 *     script reads the cookie on its own behalf when DEEL runs.
 *   - DO NOT read `_Social` or `_Performance` — neither maps to anything
 *     we currently load.
 *
 * Keep this file the single source of truth for SF cookie names so a
 * rename by State Farm is a one-place fix.
 */

export const SF_ANALYTICS_COOKIE = 'STATEFARM_ENSIGHTEN_PRIVACY_Analytics';
export const SF_MARKETING_COOKIE = 'STATEFARM_ENSIGHTEN_PRIVACY_Marketing';
export const SF_PERFORMANCE_COOKIE = 'STATEFARM_ENSIGHTEN_PRIVACY_Performance';
export const SF_SOCIAL_COOKIE = 'STATEFARM_ENSIGHTEN_PRIVACY_Social';

export type CloudCacheState = 'granted' | 'denied' | 'unknown';

/**
 * Find a cookie's value by name in document.cookie. Returns the raw
 * value (URL-decoded) or undefined if missing / blocked. Tolerates
 * leading/trailing whitespace per the cookie-string grammar.
 */
function readCookie(name: string): string | undefined {
  try {
    const cookies = document.cookie;
    if (!cookies) return undefined;
    const target = `${name}=`;
    const parts = cookies.split(';');
    for (const raw of parts) {
      const trimmed = raw.trim();
      if (trimmed.startsWith(target)) {
        return decodeURIComponent(trimmed.slice(target.length));
      }
    }
    return undefined;
  } catch {
    return undefined;
  }
}

/**
 * Read the State Farm DNS Analytics opt-out signal.
 *
 *   '0'        → 'denied'   (user opted OUT of Analytics on the DNS page)
 *   '1', else  → 'granted'  (any non-zero present value = not opted out)
 *   absent     → 'unknown'  (user has not interacted with the DNS page)
 *
 * Callers should treat 'unknown' as "no signal — do nothing"; only act on
 * a definitive 'denied' to avoid spuriously denying analytics for users
 * who simply never set a preference.
 */
export function readCloudCacheAnalytics(): CloudCacheState {
  const value = readCookie(SF_ANALYTICS_COOKIE);
  if (value === undefined) return 'unknown';
  if (value === '0') return 'denied';
  return 'granted';
}

/**
 * Read the State Farm DNS Marketing (Online Behavioral Advertising) opt-out
 * signal. Same value semantics as readCloudCacheAnalytics. Used solely as a
 * load gate for the SF enterprise GTM container (consent.ts:syncSfGtm) —
 * the container carries retargeting tags, so SF's Marketing category is its
 * "applicable privacy indicator" per SF guidance (2026-07).
 */
export function readCloudCacheMarketing(): CloudCacheState {
  const value = readCookie(SF_MARKETING_COOKIE);
  if (value === undefined) return 'unknown';
  if (value === '0') return 'denied';
  return 'granted';
}
